import { useState } from 'react';
import { ChevronUp, ExternalLink, Cloud, Database, Shield, Brain, Lock, Zap } from 'lucide-react';

interface AzureService {
  name: string;
  description: string;
  icon: typeof Cloud;
}

const azureServices: AzureService[] = [
  { name: 'Azure Static Web Apps', description: 'Hosting this application', icon: Cloud },
  { name: 'Azure Functions', description: 'Serverless API backend', icon: Zap },
  { name: 'Azure SQL Database', description: 'Metadata & chain of custody', icon: Database },
  { name: 'Azure Blob Storage', description: 'WORM evidence storage', icon: Lock },
  { name: 'Azure AI Services', description: 'Video Indexer & Document Intelligence', icon: Brain },
  { name: 'Azure Key Vault', description: 'Secrets management', icon: Shield },
];

export function PoweredByAzure() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-20">
      {/* Expandable services panel */}
      {isExpanded && (
        <div className="bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Azure Services Powering DEMS</h3>
              <a
                href="https://azure.microsoft.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Learn more <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {azureServices.map((service) => (
                <div 
                  key={service.name}
                  className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors"
                >
                  <service.icon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 leading-tight">{service.name}</p>
                    <p className="text-xs text-gray-500 leading-tight">{service.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer bar */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
          {/* Left side - Microsoft branding */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity group"
          >
            {/* Microsoft logo - 2x2 grid */}
            <div className="grid grid-cols-2 gap-0.5">
              <div className="w-2.5 h-2.5 bg-[#F25022]"></div>
              <div className="w-2.5 h-2.5 bg-[#7FBA00]"></div>
              <div className="w-2.5 h-2.5 bg-[#00A4EF]"></div>
              <div className="w-2.5 h-2.5 bg-[#FFB900]"></div>
            </div>
            <span className="text-xs font-medium text-gray-300">
              Powered by <span className="text-white">Microsoft Azure</span>
            </span>
            <ChevronUp className={`h-3 w-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Center - Key compliance badges */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Shield className="h-3 w-3" />
              <span>FedRAMP High</span>
            </div>
            <div className="h-3 w-px bg-gray-700" />
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Lock className="h-3 w-3" />
              <span>CJIS Compliant</span>
            </div>
            <div className="h-3 w-px bg-gray-700" />
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Database className="h-3 w-3" />
              <span>SOC 2 Type II</span>
            </div>
          </div>

          {/* Right side - Platform message */}
          <div className="text-xs text-gray-400 hidden lg:block">
            <span className="italic">"Microsoft IS the platform"</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoweredByAzure;
